"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Rocket, Palette, Search, PenTool, ArrowUpRight, Check, Sparkles } from "lucide-react";

type ServiceKey = "startup" | "logo" | "seo" | "content";

const services: Array<{
  key: ServiceKey;
  icon: typeof Rocket;
  label: string;
  hint: string;
}> = [
  { key: "startup", icon: Rocket, label: "Full clothing-brand launch", hint: "End-to-end" },
  { key: "logo", icon: Palette, label: "Logo & brand identity", hint: "From £29" },
  { key: "seo", icon: Search, label: "SEO retainer", hint: "Monthly" },
  { key: "content", icon: PenTool, label: "Content writing", hint: "Monthly" },
];

type StageKey = "pre" | "soon" | "live";

const stages: Array<{ key: StageKey; label: string }> = [
  { key: "pre", label: "Pre-launch — idea + research" },
  { key: "soon", label: "Launching in the next 90 days" },
  { key: "live", label: "Live, growing, need help scaling" },
];

type BudgetKey = "small" | "mid" | "large" | "open";

const budgets: Array<{ key: BudgetKey; label: string }> = [
  { key: "small", label: "Up to £3k" },
  { key: "mid", label: "£3k – £8k" },
  { key: "large", label: "£8k – £15k" },
  { key: "open", label: "Over £15k / Open" },
];

const serviceRanges: Record<ServiceKey, [number, number]> = {
  startup: [4500, 18000],
  logo: [80, 250],
  seo: [600, 3000],
  content: [320, 1200],
};

const stageMultiplier: Record<StageKey, number> = {
  pre: 1.1,
  soon: 1.0,
  live: 0.85,
};

function formatGBP(n: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
}

export function QuoteEstimator() {
  const [selected, setSelected] = useState<Set<ServiceKey>>(new Set());
  const [stage, setStage] = useState<StageKey | null>(null);
  const [budget, setBudget] = useState<BudgetKey | null>(null);

  const toggleService = (key: ServiceKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const estimate = useMemo(() => {
    if (selected.size === 0 || !stage) return null;
    const m = stageMultiplier[stage];
    let lo = 0;
    let hi = 0;
    selected.forEach((k) => {
      lo += serviceRanges[k][0];
      hi += serviceRanges[k][1];
    });
    return { lo: Math.round(lo * m), hi: Math.round(hi * m) };
  }, [selected, stage]);

  const reset = () => {
    setSelected(new Set());
    setStage(null);
    setBudget(null);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl p-6 lg:p-10"
      style={{
        backgroundColor: "#0D0D0D",
        border: "1px solid #1E1E1E",
      }}
    >
      {/* Subtle dot grid pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(rgba(191,247,69,0.6) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.03,
        }}
      />

      <div className="relative">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="text-small font-semibold uppercase tracking-[0.2em] text-accent">
              <span aria-hidden="true" className="mr-2">*</span>Ballpark in 60 seconds
            </p>
            <h2 className="text-h3 mt-2">
              Estimate the project before you{" "}
              <span className="text-accent">get on a call.</span>
            </h2>
          </div>
          {(selected.size > 0 || stage || budget) && (
            <button
              type="button"
              onClick={reset}
              className="text-small text-text-muted underline-offset-4 hover:text-accent hover:underline"
            >
              Reset
            </button>
          )}
        </div>
        <p className="mt-3 text-body text-text-muted max-w-2xl">
          Pick what you need below. Numbers are wide on purpose — this is a
          ballpark, not a quote. The real number lands on the scoping call
          once we know your product count and timeline.
        </p>

        {/* Step 1 — services (multi) */}
        <fieldset className="mt-10">
          <StepLabel num={1} text="What do you need?" hint="(pick any)" />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {services.map((s) => {
              const isOn = selected.has(s.key);
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={isOn}
                  onClick={() => toggleService(s.key)}
                  className="group flex items-center gap-3 rounded-lg p-4 text-left transition-all duration-200 hover:-translate-y-[3px]"
                  style={
                    isOn
                      ? {
                          backgroundColor: "#111111",
                          border: "1px solid #bff745",
                          boxShadow:
                            "0 0 0 1px rgba(191,247,69,0.25), 0 0 32px rgba(191,247,69,0.18)",
                        }
                      : {
                          backgroundColor: "#0a0a0a",
                          border: "1px solid #1E1E1E",
                        }
                  }
                >
                  <span
                    className="inline-flex size-10 items-center justify-center rounded-md shrink-0 transition-all duration-200"
                    style={
                      isOn
                        ? {
                            backgroundColor: "#bff745",
                            color: "#000000",
                            boxShadow: "0 0 20px rgba(191,247,69,0.55)",
                          }
                        : {
                            backgroundColor: "#0a0a0a",
                            border: "1px solid #1E1E1E",
                            color: "#bff745",
                          }
                    }
                  >
                    {isOn ? <Check className="size-4" strokeWidth={3} /> : <Icon className="size-4" />}
                  </span>
                  <span className="flex-1">
                    <span className="block text-body font-semibold text-text">{s.label}</span>
                    <span className="block text-small text-text-muted">{s.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Step 2 — stage (single) */}
        <fieldset className="mt-10">
          <StepLabel num={2} text="Where are you at?" />
          <div className="mt-5 flex flex-wrap gap-2.5">
            {stages.map((s) => (
              <PillButton
                key={s.key}
                isOn={stage === s.key}
                onClick={() => setStage(s.key)}
                label={s.label}
              />
            ))}
          </div>
        </fieldset>

        {/* Step 3 — budget (single, optional) */}
        <fieldset className="mt-10">
          <StepLabel num={3} text="Rough budget?" hint="(optional)" />
          <div className="mt-5 flex flex-wrap gap-2.5">
            {budgets.map((b) => (
              <PillButton
                key={b.key}
                isOn={budget === b.key}
                onClick={() => setBudget(b.key)}
                label={b.label}
              />
            ))}
          </div>
        </fieldset>

        {/* Result */}
        <div
          key={estimate ? `${estimate.lo}-${estimate.hi}` : "empty"}
          aria-live="polite"
          className="estimator-result mt-10 rounded-lg p-6 lg:p-7"
          style={{
            backgroundColor: "#111111",
            border: "1px solid #1E1E1E",
            borderLeft: "3px solid #bff745",
          }}
        >
          {estimate ? (
            <>
              <div className="flex items-center gap-2">
                <Sparkles
                  className="size-4 text-accent"
                  aria-hidden="true"
                  style={{ filter: "drop-shadow(0 0 6px rgba(191,247,69,0.55))" }}
                />
                <p className="text-small font-semibold uppercase tracking-[0.18em] text-accent">
                  Estimated range
                </p>
              </div>
              <p
                className="mt-3 text-accent font-extrabold leading-none tabular-nums"
                style={{
                  fontSize: "clamp(2rem, 4vw + 0.5rem, 3rem)",
                  textShadow: "0 0 24px rgba(191,247,69,0.35)",
                }}
              >
                {formatGBP(estimate.lo)} – {formatGBP(estimate.hi)}
              </p>
              <p className="mt-3 text-body text-text-muted">
                Ballpark for {selected.size} service{selected.size > 1 ? "s" : ""}.
                Final number depends on scope. Get on a free call and we'll
                tighten it within 24 hours.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  href="/contact#message"
                  className="inline-flex items-center gap-2 rounded-pill px-5 py-2.5 text-small font-bold transition-all hover:-translate-y-0.5"
                  style={{
                    backgroundColor: "#bff745",
                    color: "#000000",
                    boxShadow: "0 12px 28px -10px rgba(191,247,69,0.5)",
                  }}
                >
                  Book a call to confirm
                  <ArrowUpRight className="size-4" />
                </Link>
                <Link
                  href="/pricing"
                  className="inline-flex items-center gap-2 rounded-pill border border-border-strong px-5 py-2.5 text-small font-semibold text-text transition hover:border-accent hover:text-accent"
                >
                  See full pricing
                </Link>
              </div>
            </>
          ) : (
            <p className="text-body text-text-muted">
              Pick at least one service and your brand stage above to see a
              ballpark.
            </p>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes estimator-rise {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .estimator-result {
          animation: estimator-rise 400ms ease-out;
        }
      `}</style>
    </div>
  );
}

function StepLabel({
  num,
  text,
  hint,
}: {
  num: number;
  text: string;
  hint?: string;
}) {
  return (
    <legend className="flex items-baseline gap-3">
      <span
        aria-hidden="true"
        className="text-h2 font-extrabold tabular-nums leading-none text-accent shrink-0"
        style={{ letterSpacing: "-0.02em" }}
      >
        {String(num).padStart(2, "0")}
      </span>
      <span className="text-small font-semibold uppercase tracking-[0.18em] text-accent">
        <span aria-hidden="true" className="mr-2">*</span>{text}
        {hint && (
          <span className="ml-2 normal-case tracking-normal text-text-muted/70">
            {hint}
          </span>
        )}
      </span>
    </legend>
  );
}

function PillButton({
  isOn,
  onClick,
  label,
}: {
  isOn: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={isOn}
      onClick={onClick}
      className="rounded-pill px-5 py-2.5 text-small font-semibold transition-all duration-200 hover:-translate-y-0.5"
      style={
        isOn
          ? {
              backgroundColor: "#bff745",
              color: "#000000",
              border: "1px solid #bff745",
              boxShadow: "0 8px 24px -10px rgba(191,247,69,0.5)",
            }
          : {
              backgroundColor: "transparent",
              color: "#a0a0a0",
              border: "1px solid #2E2E2E",
            }
      }
    >
      {label}
    </button>
  );
}
